import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  IconButton,
  TextField,
  Avatar,
  Paper,
  Typography,
  InputAdornment,
  alpha,
} from "@mui/material";
import { X, Send, User } from "lucide-react";
import orbieeAvatar from "../../images/Orbiee_Avatra.png";
import { getUserProfile, isAuthenticated } from "../../utils/auth";
import {
  stopOrbieeVoicePlayback,
} from "../../services/websocket";
import {
  ORBIEE_AGENT_BASE_URL,
  USE_ORBIEE_WEBSOCKET_CHAT,
} from "../../config/apiConfig";
import { LOADING_MESSAGES_BY_CONTEXT } from "../IntentBasedLoading";

// Load all avatar icons dynamically using require.context (aligns with TopBar)
const avatarIconsContext = require.context("../../Avatar_Icons", false, /_Icon\.png$/);
const avatarIcons = {};
avatarIconsContext.keys().forEach((item) => {
  const iconName = item.replace("./", "").replace("_Icon.png", "");
  avatarIcons[iconName] = avatarIconsContext(item);
});

/** IntentBasedLoading keys that ChartBoxCode can show while waiting. */
const LOADING_CONTEXT_ALIASES = {
  uncertain: "default",
  billing_confirmation: "complete_purchase",
  virtual_try_on: "detail_page",
  search: "self",
  product_list: "self",
};

function getLoadingMessagesForContext(ctx) {
  const key = LOADING_CONTEXT_ALIASES[ctx] || ctx;
  const messages = LOADING_MESSAGES_BY_CONTEXT[key];
  return messages?.length
    ? messages
    : LOADING_MESSAGES_BY_CONTEXT.default;
}

function inferLoadingContext(messages, pathname) {
  const lastUser = [...(messages || [])]
    .reverse()
    .find((m) => m?.isUser);
  const query = String(lastUser?.content || "").toLowerCase();

  if (/\b(add(\s+to)?\s+cart)\b/.test(query)) return "add_to_cart";
  if (/\b(cart|bag|basket)\b/.test(query)) return "view_cart";
  if (/\b(confirm(\s+\w+)?\s*(purchase|order|payment))\b/.test(query)) {
    return "confirm_purchase";
  }
  if (/\b(buy|purchase|checkout|pay|payment)\b/.test(query)) {
    return "complete_purchase";
  }
  if (/\b(detail|info|spec|tell.*about)\b/.test(query)) return "detail_page";
  if (/\b(gift|present|someone else)\b/.test(query)) return "others";

  const path = String(pathname || "");
  if (path.startsWith("/cart")) return "view_cart";
  if (path.startsWith("/checkout") || path.startsWith("/order-confirmation")) {
    return "complete_purchase";
  }
  if (
    path.startsWith("/product/") ||
    path.startsWith("/item/") ||
    path.startsWith("/recommendations")
  ) {
    return "detail_page";
  }

  return "self";
}

/**
 * ChartBoxCode Component
 * MUI-based chat box component for interacting with Orbiee chatbot
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether chat box is open
 * @param {Array} props.messages - Array of message objects
 * @param {string} props.inputValue - Current input value
 * @param {Function} props.setInputValue - Function to set input value
 * @param {Function} props.onClose - Function to close chat box
 * @param {Function} props.onSendMessage - Function to send message
 * @param {Function} props.onKeyPress - Function to handle key press
 * @param {boolean} props.isCartCollapsed - Whether cart is collapsed
 * @param {Function} props.setIsCartCollapsed - Function to toggle cart
 * @param {number} props.totalItems - Total items in cart
 * @param {Function} props.onSuggestionClick - Function to handle suggestion click
 * @param {boolean} props.isLoading - Whether loading
 * @param {boolean} [props.listenAvatarEvents=true] - Surface avatar background chat in this box
 */
export default function ChartBoxCode({
  isOpen = false,
  messages = [],
  inputValue = "",
  setInputValue,
  onClose,
  onSendMessage,
  onKeyPress,
  isCartCollapsed = true,
  setIsCartCollapsed,
  totalItems = 0,
  onSuggestionClick,
  isLoading = false,
  listenAvatarEvents = true,
}) {
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(getUserProfile());
  const [profileImageError, setProfileImageError] = useState(false);
  /** WebSocket agent backend reachability (GET /health on ORBIEE_AGENT_BASE_URL). */
  const [agentWsStatus, setAgentWsStatus] = useState("unknown");

  // Check if user is logged in
  useEffect(() => {
    const checkLoginStatus = () => {
      const profile = getUserProfile();
      setUserProfile(profile);
      setProfileImageError(false);
      setIsUserLoggedIn(isAuthenticated() && !!profile?.customer_name);
    };

    checkLoginStatus();

    // Listen for auth state changes
    const handleAuthChange = () => {
      setTimeout(checkLoginStatus, 100);
    };

    window.addEventListener("authStateChange", handleAuthChange);
    window.addEventListener("authStateChanged", handleAuthChange);
    return () => {
      window.removeEventListener("authStateChange", handleAuthChange);
      window.removeEventListener("authStateChanged", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    if (!USE_ORBIEE_WEBSOCKET_CHAT || !ORBIEE_AGENT_BASE_URL) {
      setAgentWsStatus("unknown");
      return undefined;
    }

    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    const base = ORBIEE_AGENT_BASE_URL.replace(/\/$/, "");
    const healthUrl = `${base}/health`;

    const probe = async () => {
      setAgentWsStatus((prev) => (prev === "unknown" ? "checking" : prev));
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => ctrl.abort(), 4000);
      try {
        const res = await fetch(healthUrl, {
          method: "GET",
          signal: ctrl.signal,
          cache: "no-store",
        });
        if (!cancelled) {
          setAgentWsStatus(res.ok ? "online" : "offline");
        }
      } catch {
        if (!cancelled) {
          setAgentWsStatus("offline");
        }
      } finally {
        window.clearTimeout(timer);
      }
    };

    probe();
    const id = window.setInterval(probe, 15000);

    const onVisibility = () => {
      if (document.visibilityState === "visible") probe();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isOpen]);

  const [avatarMessages, setAvatarMessages] = useState([]);
  const messagesRef = useRef(messages);
  const avatarMessagesRef = useRef([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const getProfilePictureUrl = useCallback(() => {
    if (!userProfile?.customer_name) return null;

    const userfullname = userProfile.customer_name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ".");

    return avatarIcons[userfullname] || null;
  }, [userProfile]);

  const loadingContext = useMemo(
    () => inferLoadingContext(messages, location.pathname),
    [messages, location.pathname]
  );
  const loadingMessages = useMemo(
    () => getLoadingMessagesForContext(loadingContext),
    [loadingContext]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, avatarMessages, isLoading, loadingMessageIndex]);

  // Stop playback on unmount
  useEffect(() => {
    return () => {
      stopOrbieeVoicePlayback();
    };
  }, []);

  // Cycle through context-specific loading messages while waiting.
  useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0);
      return;
    }

    setLoadingMessageIndex(0);
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [isLoading, loadingMessages]);

  // Listen for background avatar chat events to surface them inside the chat box.
  useEffect(() => {
    if (!listenAvatarEvents) return undefined;

    const dedupeExists = (msg) => {
      const allMessages = [...messagesRef.current, ...avatarMessagesRef.current];
      const msgText =
        msg.content || msg.conversation_text || msg.answer || msg.followup || "";
      return allMessages.some((m) => {
        const text =
          m.content || m.conversation_text || m.answer || m.followup || "";
        return m.isUser === msg.isUser && text === msgText;
      });
    };

    const handleBackgroundChat = (event) => {
      const { userText, botText, botTexts } = event.detail || {};
      const additions = [];

      if (userText) {
        const userMsg = {
          id: `avatar-user-${Date.now()}`,
          content: userText,
          isUser: true,
          timestamp: new Date(),
        };
        if (!dedupeExists(userMsg)) {
          additions.push(userMsg);
        }
      }

      const texts = Array.isArray(botTexts)
        ? botTexts.map((t) => String(t ?? "").trim()).filter(Boolean)
        : botText
          ? [String(botText).trim()].filter(Boolean)
          : [];

      texts.forEach((text, i) => {
        const botMsg = {
          id: `avatar-bot-${Date.now()}-${i}`,
          conversation_text: text,
          answer: text,
          isUser: false,
          timestamp: new Date(),
        };
        if (!dedupeExists(botMsg)) {
          additions.push(botMsg);
        }
      });

      if (additions.length) {
        setAvatarMessages((prev) => {
          const next = [...prev, ...additions];
          avatarMessagesRef.current = next;
          return next;
        });
      }
    };

    window.addEventListener("orbieeBackgroundChat", handleBackgroundChat);
    return () =>
      window.removeEventListener("orbieeBackgroundChat", handleBackgroundChat);
  }, [listenAvatarEvents]);

  const displayedMessages = useMemo(
    () => [...messages, ...avatarMessages],
    [messages, avatarMessages]
  );

  const handleKeyPress = (e) => {
    // Pass key press to parent component to handle
    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 64, // Position below the top bar (h-16 = 64px)
        right: { xs: 0, sm: 12 }, // Add small margin on right for curved corners
        zIndex: 1300,
        height: "calc(100vh - 64px)", // Subtract top bar height
        display: "flex",
        flexDirection: "column",
        bgcolor: "white",
        boxShadow: { xs: "none", sm: "-2px 0 8px rgba(0, 0, 0, 0.08)" }, // Reduced drop shadow
        borderLeft: "none", // Remove the black border line
        overflow: "hidden",
        width: { xs: "100%", sm: 400 },
        maxWidth: { xs: "100%", sm: "35vw" },
        borderRadius: { xs: 0, sm: "16px" }, // Curved edges on all 4 corners
        // Slide-in animation like browser DevTools
        transform: isOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        // Hide pointer events when closed to allow interaction with content behind
        pointerEvents: isOpen ? "auto" : "none",
        fontFamily: "'Open Sans', sans-serif", // Apply Open Sans font to entire chat
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          borderBottom: "1px solid",
          borderColor: alpha("#000000", 0.05),
          bgcolor: "#ffffff",
          flexShrink: 0,
        }}
      >
        {/* Left Side - Avatar, Name, and Status */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
          {/* Avatar with blue ring and green circle effect */}
          <Box
            sx={{
              position: "relative",
              width: 40,
              height: 40,
            }}
          >
            {/* Blue outer ring */}
            <Box
              sx={{
                position: "absolute",
                top: -2,
                left: -2,
                width: 44,
                height: 44,
                borderRadius: "50%",
                border: "2px solid #2196F3",
                zIndex: 1,
              }}
            />
            {/* Green inner circle */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "#4CAF50",
                zIndex: 2,
              }}
            />
            {/* Avatar image */}
            <Avatar
              src={orbieeAvatar}
              alt="Orbiee Assistant"
              sx={{
                width: 40,
                height: 40,
                position: "relative",
                zIndex: 3,
                border: "2px solid transparent",
              }}
            />
          </Box>

          {/* Name and WebSocket agent / login status */}
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: "text.primary",
                fontSize: "0.9375rem",
                lineHeight: 1.2,
              }}
            >
              Orbiee
            </Typography>
           
          </Box>
        </Box>

        {/* Right Side - Icons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Refresh Button */}
          {/* <IconButton
            onClick={() => {
              // Refresh functionality - you can add custom logic here
              window.location.reload();
            }}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha("#000000", 0.05),
                color: "text.primary",
              },
            }}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw style={{ width: 20, height: 20 }} />
          </IconButton> */}

          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha("#000000", 0.05),
                color: "text.primary",
              },
            }}
            aria-label="Close chat"
            title="Close chat"
          >
            <X style={{ width: 20, height: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.5, sm: 2 },
          bgcolor: "transparent",
          minHeight: 0, // Allows flexbox to properly shrink
        }}
      >
        {displayedMessages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: "flex",
              gap: 1.5,
              justifyContent: msg.isUser ? "flex-end" : "flex-start",
              alignItems: "flex-start",
            }}
          >
            {/* Bot Avatar - Left side */}
            {!msg.isUser && (
              <Avatar
                src={orbieeAvatar}
                alt="Orbiee Avatar"
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  flexShrink: 0,
                }}
              />
            )}

            {/* Message Bubble */}
            <Box
              sx={{
                maxWidth: { xs: "75%", sm: "80%" },
                display: "flex",
                alignItems: "flex-start",
                gap: { xs: 0.75, sm: 1 },
                flexDirection: msg.isUser ? "row-reverse" : "row",
              }}
            >
              {msg.isUser && (
                isUserLoggedIn && userProfile && getProfilePictureUrl() && !profileImageError ? (
                  <Box
                    component="img"
                    src={getProfilePictureUrl()}
                    alt="User Avatar"
                    onError={() => setProfileImageError(true)}
                    sx={{
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                      flexShrink: 0,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                      flexShrink: 0,
                      bgcolor: "#E5E7EB",
                      color: "#4B5563",
                    }}
                  >
                    <User style={{ width: 18, height: 18 }} />
                  </Avatar>
                )
              )}
              {msg.isUser ? (
                // User Message - Blue bubble with sharp bottom-right corner pointing to avatar
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#2B7FFF",
                    color: "white",
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                    borderRadius: "16px 16px 16px 4px", // Top-left, top-right, bottom-left rounded, bottom-right sharp
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-line",
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      lineHeight: 1.5,
                      fontFamily: "'Open Sans', sans-serif",
                    }}
                  >
                    {msg.content}
                  </Typography>
                </Paper>
              ) : (
                // Bot Message - Light grey bubble with sharp bottom-left corner pointing to avatar
                <>
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: "#F3F4F6",
                      border: "none",
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 1, sm: 1.5 },
                      borderRadius: "16px 16px 4px 16px", // Top-left, top-right, bottom-left sharp, bottom-right rounded
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      flex: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#000000",
                        whiteSpace: "pre-line",
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                        lineHeight: { xs: 1.5, sm: 1.6 },
                        fontFamily: "'Open Sans', sans-serif",
                      }}
                    >
                      {msg.conversation_text || msg.answer || ""}
                    </Typography>

                    {/* Follow-up Question with Suggested Answers */}
                    {(msg.followup_question?.question || msg.followup) && (
                      <Box sx={{ mt: 2 }}>
                        <Box
                          sx={{
                            borderLeft: "4px solid",
                            borderColor: "primary.main",
                            pl: 1.5,
                            py: 1,
                            bgcolor: alpha("#2196f3", 0.1),
                            borderRadius: 1,
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#000000",
                              fontFamily: "'Open Sans', sans-serif",
                            }}
                          >
                            {msg.followup_question?.question || msg.followup}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 0.75, sm: 1 } }}>
                          {msg.followup_question?.suggested_answers?.map(
                            (answer, idx) => (
                              <Box
                                key={idx}
                                component="button"
                                onClick={() => onSuggestionClick && onSuggestionClick(answer)}
                                sx={{
                                  px: 1.5,
                                  py: 0.75,
                                  fontSize: "0.75rem",
                                  bgcolor: "grey.100",
                                  color: "text.primary",
                                  border: "1px solid",
                                  borderColor: "grey.300",
                                  borderRadius: "9999px",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    bgcolor: "grey.200",
                                  },
                                  textTransform: "none",
                                }}
                              >
                                {answer}
                              </Box>
                            )
                          ) || (
                              <>
                                <Box
                                  component="button"
                                  onClick={() => onSuggestionClick && onSuggestionClick("Yes")}
                                  sx={{
                                    px: { xs: 1.25, sm: 1.5 },
                                    py: { xs: 0.5, sm: 0.75 },
                                    fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                                    bgcolor: alpha("#2196f3", 0.1),
                                    color: "primary.main",
                                    border: "1px solid",
                                    borderColor: "primary.main",
                                    borderRadius: "9999px",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      bgcolor: alpha("#2196f3", 0.2),
                                    },
                                    textTransform: "none",
                                  }}
                                >
                                  Yes
                                </Box>
                                <Box
                                  component="button"
                                  onClick={() => onSuggestionClick && onSuggestionClick("No")}
                                  sx={{
                                    px: { xs: 1.25, sm: 1.5 },
                                    py: { xs: 0.5, sm: 0.75 },
                                    fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                                    bgcolor: "grey.100",
                                    color: "text.primary",
                                    border: "1px solid",
                                    borderColor: "grey.300",
                                    borderRadius: "9999px",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      bgcolor: "grey.200",
                                    },
                                    textTransform: "none",
                                  }}
                                >
                                  No
                                </Box>
                              </>
                            )}
                        </Box>
                      </Box>
                    )}
                  </Paper>

                </>
              )}
            </Box>
          </Box>
        ))}

        {isLoading && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
            role="status"
            aria-live="polite"
            aria-label="Assistant is typing"
          >
            {/* Bot Avatar */}
            <Avatar
              src={orbieeAvatar}
              alt="Orbiee Avatar"
              sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                flexShrink: 0,
              }}
            />

            {/* Loading Message Bubble */}
            <Box
              sx={{
                maxWidth: { xs: "75%", sm: "80%" },
                display: "flex",
                alignItems: "flex-start",
                gap: { xs: 0.75, sm: 1 },
                flexDirection: "row",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "#F3F4F6",
                  border: "none",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1, sm: 1.5 },
                  borderRadius: "16px 16px 4px 16px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  "@keyframes orbieeTypingBounce": {
                    "0%, 80%, 100%": {
                      transform: "translateY(0)",
                      opacity: 0.35,
                    },
                    "40%": {
                      transform: "translateY(-4px)",
                      opacity: 1,
                    },
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  aria-hidden
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "text.secondary",
                        animation: "orbieeTypingBounce 1.2s ease-in-out infinite",
                        animationDelay: `${i * 0.16}s`,
                      }}
                    />
                  ))}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.primary",
                    fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                    lineHeight: { xs: 1.5, sm: 1.6 },
                    fontFamily: "'Roboto', sans-serif",
                  }}
                >
                  {loadingMessages[loadingMessageIndex] || loadingMessages[0]}
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Footer */}
      <Box
        sx={{
          p: { xs: 1, sm: 1.5 },
          borderTop: "1px solid",
          borderColor: alpha("#000000", 0.1),
          bgcolor: "white",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            fullWidth
            value={inputValue}
            onChange={(e) => setInputValue && setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={isLoading}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#F5F5F5",
                borderRadius: "24px",
                "& fieldset": {
                  borderColor: alpha("#000000", 0.1),
                  borderWidth: 1,
                },
                "&:hover fieldset": {
                  borderColor: alpha("#000000", 0.2),
                },
                "&.Mui-focused fieldset": {
                  borderColor: alpha("#2196f3", 0.5),
                  borderWidth: 1,
                },
              },
              "& .MuiInputBase-input": {
                py: { xs: 1.25, sm: 1.5 },
                px: { xs: 2, sm: 2.5 },
                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                color: "#000000",
                "&::placeholder": {
                  color: alpha("#000000", 0.5),
                  opacity: 1,
                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                },
              },
            }}
            InputProps={{
              endAdornment: inputValue.trim() && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => onSendMessage && onSendMessage()}
                    disabled={isLoading || !inputValue.trim()}
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        bgcolor: alpha("#2196f3", 0.1),
                      },
                      "&.Mui-disabled": {
                        color: "action.disabled",
                      },
                    }}
                    aria-label="Send message"
                    title="Send message"
                  >
                    <Send style={{ width: 20, height: 20 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
